/**
 * NonlinearTempPage.tsx — Non-Linear Temperature Stresses
 *
 * Live tool: the user shapes a temperature profile through the PCC slab (by
 * dragging the curve or picking a preset) and immediately sees the resulting
 * self-equilibrating stresses at the surface and bottom of the slab.
 *
 * Only the physical inputs and the stresses are surfaced. The internal
 * decomposition machinery (neutral axis, effective thickness, moments, the
 * constant/linear temperature components) stays hidden; the one intermediate
 * worth showing — the equivalent linear ΔT that MEPDG-style tools consume — is
 * reported alongside the stresses.
 *
 * Design tokens and layout conventions mirror PittRigidPage / MnLayerPage.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import {
  computeNonlinTemp,
  curvedProfile,
  linearProfile,
  N_NODES,
  type Bond,
} from '../hooks/nonlinTemp';

const monoFont  = { fontFamily: "'DM Mono', monospace" };
const serifFont = { fontFamily: "'Crimson Pro', Georgia, serif" };

const inputCls = 'w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#003594]/30 focus:border-[#003594] transition-colors';

const PITT_BLUE = '#003594';
const PITT_NAVY = '#001f5b';
const PITT_GOLD = '#FFB81C';

type PresetKey = 'daytime' | 'nighttime' | 'linear' | 'custom';

/** Concrete slab defaults; base defaults to a soft, thick granular layer (US customary). */
const DEFAULTS = {
  E1: 4_000_000, // psi
  mu: 0.15,
  E2: 30_000,    // psi  (soft granular base)
  alpha: 5.0e-6, // 1/°F
  h1: 10.0,      // in
  h2: 20.0,      // in
};

// Realistic daytime positive gradient (surface hot, concave), °F.
const DEFAULT_PROFILE = curvedProfile(97, 75, 1.6).map(v => Math.round(v * 2) / 2);

// Group the integer part with thousands separators, preserving any decimals as typed.
function formatNum(n: number): string {
  if (!isFinite(n)) return '';
  const s = String(n);
  if (s.includes('e') || s.includes('E')) return s; // leave scientific notation alone
  const neg = s.startsWith('-');
  const abs = neg ? s.slice(1) : s;
  const dot = abs.indexOf('.');
  const intPart = dot === -1 ? abs : abs.slice(0, dot);
  const decPart = dot === -1 ? '' : abs.slice(dot);
  return (neg ? '-' : '') + intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + decPart;
}

// ─── NumInput (from the app's shared pattern) ────────────────────────────────
const NumInput: React.FC<{
  value: number; onChange: (n: number) => void;
  className?: string; step?: string; min?: string; max?: string;
}> = ({ value, onChange, className, step, min, max }) => {
  const [draft, setDraft] = useState(formatNum(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(formatNum(value)); }, [value]);
  const commit = () => {
    focused.current = false;
    const n = parseFloat(draft.replace(/,/g, ''));
    if (!isNaN(n)) { onChange(n); setDraft(formatNum(n)); } else setDraft(formatNum(value));
  };
  return (
    <input type="text" inputMode="decimal" step={step} min={min} max={max}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onFocus={() => { focused.current = true; setDraft(d => d.replace(/,/g, '')); }}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={className ?? inputCls}
    />
  );
};

const Field: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {children}
  </div>
);

const SectionHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <h3 style={serifFont} className="text-base font-semibold text-[#001f5b] whitespace-nowrap">{children}</h3>
    <div className="flex-1 border-t border-gray-200" />
  </div>
);

// ─── Thermal color ramp (cool → warm) ────────────────────────────────────────
const RAMP: [number, number, number][] = [
  [40, 74, 138],   // deep blue  (coolest)
  [70, 140, 190],
  [120, 190, 205],
  [235, 214, 130],
  [224, 122, 60],
  [193, 59, 42],    // deep red   (hottest)
];
function tempColor(t: number, tMin: number, tMax: number): string {
  const f = tMax > tMin ? (t - tMin) / (tMax - tMin) : 0.5;
  const p = Math.max(0, Math.min(1, f)) * (RAMP.length - 1);
  const i = Math.min(RAMP.length - 2, Math.floor(p));
  const u = p - i;
  const a = RAMP[i], b = RAMP[i + 1];
  const r = Math.round(a[0] + (b[0] - a[0]) * u);
  const g = Math.round(a[1] + (b[1] - a[1]) * u);
  const bl = Math.round(a[2] + (b[2] - a[2]) * u);
  return `rgb(${r},${g},${bl})`;
}

// ─── Instructions / documentation ────────────────────────────────────────────
const Instructions: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.06)] overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50/60 transition-colors">
        <div className="flex items-center gap-3">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" className="text-[#003594] flex-shrink-0">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span style={serifFont} className="font-semibold text-[#001f5b] text-lg">About This Tool &amp; Method</span>
        </div>
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"
          className={`text-gray-400 transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''}`}>
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-gray-100 pt-5 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            A temperature profile through a concrete slab rarely varies linearly with depth.
            Following the equivalent-temperature approach, the profile is separated into three
            parts by the horizontal strain each produces: a constant-strain part (uniform
            expansion), a linear-strain part (curling), and a non-linear-strain part. Only the
            non-linear part cannot be relieved by free expansion or curling, so it locks in a
            self-equilibrating stress even with no external load. This tool reports that stress
            at the top and bottom of the slab.
          </p>
          <p className="text-sm text-gray-600 leading-relaxed">
            The slab and base are handled by the <strong>transformed-section method</strong>: the two
            layers are combined into one equivalent homogeneous plate whose effective thickness
            hₑ carries the same flexural stiffness, and the stresses are evaluated on that
            section. Because hₑ depends on the modulus ratio, a thick but soft base adds only a
            little effective thickness — a 10″ slab on a 20″ soft base may behave like an ~11″
            plate. Two assumptions come with this: the base carries no temperature gradient (it
            is taken as isothermal at the PCC-bottom temperature), and both layers share the same
            coefficient of thermal expansion, which makes the stress-free reference temperature
            drop out. Because the base restrains curling, a purely linear gradient can still
            register a non-zero stress.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'What you set', items: [
                { term: 'Temperature profile', def: 'Slab temperatures from surface to bottom, °F. Drag the curve or pick a preset.' },
                { term: 'Bond condition', def: 'Bonded = full composite action with the base; unbonded = frictionless interface.' },
                { term: 'Slab / base thickness', def: 'Surface-layer and base-layer thickness, in.' },
              ]},
              { title: 'Material properties', items: [
                { term: 'PCC / base modulus', def: 'Elastic modulus of the slab and the base layer, psi.' },
                { term: 'CTE', def: 'Coefficient of thermal expansion of the slab, ×10⁻⁶/°F.' },
                { term: "Poisson's ratio", def: 'Lateral-to-axial strain ratio for the slab concrete.' },
              ]},
              { title: 'What you get', items: [
                { term: 'Surface / bottom stress', def: 'Self-equilibrating stress at the two fibers, psi. Tension positive.' },
                { term: 'Equivalent linear ΔT', def: 'Straight-line gradient that MEPDG-style tools consume for curling analysis.' },
                { term: 'Stress profile', def: 'The locked-in stress at every depth through the slab.' },
              ]},
            ].map(({ title, items }) => (
              <div key={title} className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                <h3 style={serifFont} className="font-semibold text-[#001f5b] text-base mb-3">{title}</h3>
                {items.map(({ term, def }) => (
                  <div key={term}>
                    <span style={monoFont} className="text-xs font-medium text-[#003594]">{term}</span>
                    <p className="text-xs text-gray-500 leading-relaxed mt-0.5">{def}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 space-y-2">
            <p style={monoFont} className="text-xs text-[#003594] uppercase tracking-widest">Method</p>
            <p className="text-sm text-gray-600 leading-relaxed">
              The constant part is the strain-weighted mean temperature; the linear part is the
              first moment of temperature about the transformed neutral axis. The self-equilibrating
              stress at each depth is σ = −E·α/(1−μ)·T<sub>nl</sub>, where T<sub>nl</sub> is what
              remains of the profile after the constant and linear parts are removed over the
              transformed two-layer section of effective thickness hₑ.
            </p>
            <p className="text-xs text-gray-500 leading-relaxed">
              Implements the equivalent-temperature and transformed-section formulation for
              multi-layered concrete pavements (Khazanovich; building on Ioannides et al. 1992,
              Choubane &amp; Tia 1992, and Korenev &amp; Chernigovskaya 1962). Ported from the
              reference workbook and verified against it.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Shared figure geometry ───────────────────────────────────────────────────
const W = 760, H = 480;
const AX0 = 62, AX1 = 340;     // temperature panel x-range
const BX0 = 452, BX1 = 720;    // stress panel x-range
const PT = 56, PB = 430;       // plot top / bottom (depth axis)

const niceStress = (m: number) => {
  if (m <= 1) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(m)));
  const n = m / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
};

// ─── The signature figure: profile editor + stress profile ───────────────────
const SlabFigure: React.FC<{
  T: number[]; h1: number; h2: number; heff: number;
  sigma: number[]; sigmaMax: number; x: number;
  signTop: number; signBot: number;
  domain: [number, number];
  onDrag: (node: number, temp: number) => void;
  onDragEnd: () => void;
}> = ({ T, h1, h2, heff, sigma, sigmaMax, x, signTop, signBot, domain, onDrag, onDragEnd }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragNode = useRef<number | null>(null);
  const [tMin, tMax] = domain;

  // Depth axis is the EFFECTIVE (transformed) thickness hₑ. The PCC keeps its
  // physical thickness h₁ at the top; the base collapses to its transformed
  // mechanical contribution (hₑ − h₁), shown isothermal at the PCC-bottom temp.
  const depthToY = (z: number) => PT + (z / heff) * (PB - PT);
  const tempToX  = (t: number) => AX0 + ((t - tMin) / (tMax - tMin || 1)) * (AX1 - AX0);
  const sMax = Math.max(niceStress(sigmaMax), 1);
  const BXM = (BX0 + BX1) / 2;
  const stressToX = (s: number) => BXM + (Math.max(-sMax, Math.min(sMax, s)) / sMax) * ((BX1 - BX0) / 2);

  const cMin = Math.min(...T), cMax = Math.max(...T);
  const slabTopY = depthToY(0), slabBotY = depthToY(h1), effBotY = depthToY(heff);

  const toSvg = (e: React.PointerEvent) => {
    const svg = svgRef.current!;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const p = pt.matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };
  const handleMove = (e: React.PointerEvent) => {
    if (dragNode.current === null) return;
    const { x: sx } = toSvg(e);
    const t = tMin + ((sx - AX0) / (AX1 - AX0)) * (tMax - tMin);
    onDrag(dragNode.current, Math.max(tMin, Math.min(tMax, t)));
  };
  const endDrag = () => { if (dragNode.current !== null) { dragNode.current = null; onDragEnd(); } };

  const tempPath = T.map((t, j) => `${j === 0 ? 'M' : 'L'}${tempToX(t).toFixed(1)},${depthToY(j / 10 * h1).toFixed(1)}`).join(' ');
  const stressPath = sigma.map((s, j) => `${j === 0 ? 'M' : 'L'}${stressToX(s).toFixed(1)},${depthToY(j / 10 * h1).toFixed(1)}`).join(' ');
  const stressFill = `M${stressToX(0)},${slabTopY} ` +
    sigma.map((s, j) => `L${stressToX(s).toFixed(1)},${depthToY(j / 10 * h1).toFixed(1)}`).join(' ') +
    ` L${stressToX(0)},${slabBotY} Z`;

  const tTicks = Array.from({ length: 5 }, (_, i) => tMin + (tMax - tMin) * i / 4);
  const sTicks = [-sMax, -sMax / 2, 0, sMax / 2, sMax];

  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%"
      style={{ touchAction: 'none', userSelect: 'none' }}
      onPointerMove={handleMove} onPointerUp={endDrag} onPointerLeave={endDrag}>
      <defs>
        <linearGradient id="slabTemp" x1="0" y1={slabTopY} x2="0" y2={slabBotY} gradientUnits="userSpaceOnUse">
          {T.map((t, j) => <stop key={j} offset={`${j * 10}%`} stopColor={tempColor(t, cMin, cMax)} />)}
        </linearGradient>
        <pattern id="baseHatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#ffffff" strokeWidth="1.2" opacity="0.5" />
        </pattern>
      </defs>

      {/* Panel titles */}
      <text x={(AX0 + AX1) / 2} y="26" textAnchor="middle" style={serifFont} fontSize="14" fontWeight="600" fill={PITT_NAVY}>Temperature through slab</text>
      <text x={(BX0 + BX1) / 2} y="26" textAnchor="middle" style={serifFont} fontSize="14" fontWeight="600" fill={PITT_NAVY}>Locked-in stress</text>
      <text x={(AX0 + AX1) / 2} y="42" textAnchor="middle" style={monoFont} fontSize="9.5" fill="#9aa4b2">°F · drag the curve</text>
      <text x={(BX0 + BX1) / 2} y="42" textAnchor="middle" style={monoFont} fontSize="9.5" fill="#9aa4b2">psi · tension +</text>

      {/* ── Left panel: PCC gradient + transformed base + temperature curve ── */}
      {/* PCC slab — physical thickness h₁, carrying the gradient */}
      <rect x={AX0} y={slabTopY} width={AX1 - AX0} height={slabBotY - slabTopY} fill="url(#slabTemp)" opacity="0.92" />
      {/* Transformed base contribution (hₑ − h₁) — isothermal at the PCC-bottom temp */}
      <rect x={AX0} y={slabBotY} width={AX1 - AX0} height={effBotY - slabBotY} fill={tempColor(T[10], cMin, cMax)} opacity="0.92" />
      <rect x={AX0} y={slabBotY} width={AX1 - AX0} height={effBotY - slabBotY} fill="url(#baseHatch)" />
      <rect x={AX0} y={slabTopY} width={AX1 - AX0} height={effBotY - slabTopY} fill="none" stroke="#c3ccd8" strokeWidth="1" />
      <line x1={AX0} y1={slabBotY} x2={AX1} y2={slabBotY} stroke="#5c6675" strokeWidth="1" strokeDasharray="4 3" />
      <text x={AX0 + 6} y={slabTopY + 15} style={monoFont} fontSize="10" fill="#fff" fontWeight="500">PCC slab</text>
      {effBotY - slabBotY > 16 && (
        <text x={AX0 + 6} y={slabBotY + 14} style={monoFont} fontSize="9" fill="#fff" opacity="0.85">base · isothermal</text>
      )}

      {/* hₑ dimension bracket (governing effective thickness) */}
      <g stroke={PITT_BLUE} strokeWidth="1" opacity="0.85">
        <line x1="42" y1={slabTopY} x2="42" y2={effBotY} />
        <line x1="38" y1={slabTopY} x2="46" y2={slabTopY} />
        <line x1="38" y1={effBotY} x2="46" y2={effBotY} />
      </g>
      <text x="30" y={(slabTopY + effBotY) / 2} textAnchor="middle" style={monoFont} fontSize="9" fill={PITT_BLUE}
        transform={`rotate(-90 30 ${(slabTopY + effBotY) / 2})`}>hₑ = {heff.toFixed(1)}″</text>

      {/* Neutral axis */}
      <line x1={AX0} y1={depthToY(x)} x2={AX1} y2={depthToY(x)} stroke={PITT_BLUE} strokeWidth="1" strokeDasharray="2 3" opacity="0.65" />
      <text x={AX1 - 4} y={depthToY(x) - 4} textAnchor="end" style={monoFont} fontSize="8.5" fill={PITT_BLUE} opacity="0.8">neutral axis</text>

      {/* Temperature axis (top) */}
      {tTicks.map((t, i) => (
        <g key={i}>
          <line x1={tempToX(t)} y1={PT - 4} x2={tempToX(t)} y2={PT} stroke="#b8c0cc" strokeWidth="1" />
          <text x={tempToX(t)} y={PT - 8} textAnchor="middle" style={monoFont} fontSize="9" fill="#9aa4b2">{t.toFixed(0)}</text>
        </g>
      ))}
      <path d={tempPath} fill="none" stroke={PITT_NAVY} strokeWidth="2.25" />
      {T.map((t, j) => (
        <circle key={j} cx={tempToX(t)} cy={depthToY(j / 10 * h1)} r="6"
          fill="#fff" stroke={PITT_NAVY} strokeWidth="2" style={{ cursor: 'ew-resize' }}
          onPointerDown={(e) => { dragNode.current = j; (e.target as Element).setPointerCapture(e.pointerId); }} />
      ))}

      {/* ── Right panel: stress profile ── */}
      {sTicks.map((s, i) => (
        <g key={i}>
          <line x1={stressToX(s)} y1={PT} x2={stressToX(s)} y2={effBotY} stroke="#eef1f5" strokeWidth="1" />
          <text x={stressToX(s)} y={PT - 8} textAnchor="middle" style={monoFont} fontSize="9" fill="#9aa4b2">{s === 0 ? '0' : s.toFixed(0)}</text>
        </g>
      ))}
      <rect x={BX0} y={slabTopY} width={BX1 - BX0} height={slabBotY - slabTopY} fill="none" stroke="#e4e8ee" strokeWidth="1" />
      <path d={stressFill} fill={PITT_GOLD} opacity="0.18" />
      <line x1={stressToX(0)} y1={slabTopY} x2={stressToX(0)} y2={slabBotY} stroke="#9aa4b2" strokeWidth="1" />
      <path d={stressPath} fill="none" stroke="#c13b2a" strokeWidth="2.25" />
      <circle cx={stressToX(sigma[0])} cy={slabTopY} r="3.5" fill="#c13b2a" />
      <circle cx={stressToX(sigma[10])} cy={slabBotY} r="3.5" fill="#c13b2a" />
      <text x={stressToX(sigma[0]) + (sigma[0] >= 0 ? 8 : -8)} y={slabTopY + 4}
        textAnchor={sigma[0] >= 0 ? 'start' : 'end'} style={monoFont} fontSize="10" fontWeight="500" fill="#8f2a1d">
        {signTop >= 0 ? '+' : ''}{signTop.toFixed(0)}
      </text>
      <text x={stressToX(sigma[10]) + (sigma[10] >= 0 ? 8 : -8)} y={slabBotY - 4}
        textAnchor={sigma[10] >= 0 ? 'start' : 'end'} style={monoFont} fontSize="10" fontWeight="500" fill="#8f2a1d">
        {signBot >= 0 ? '+' : ''}{signBot.toFixed(0)}
      </text>

      {/* Shared depth axis label */}
      <text x="18" y={(PT + effBotY) / 2} textAnchor="middle" style={monoFont} fontSize="10" fill="#9aa4b2"
        transform={`rotate(-90 18 ${(PT + effBotY) / 2})`}>depth (surface → bottom)</text>

      {/* Compression / tension guide under stress panel */}
      <text x={BXM - 6} y={effBotY + 22} textAnchor="end" style={monoFont} fontSize="9" fill="#9aa4b2">← compression</text>
      <text x={BXM + 6} y={effBotY + 22} textAnchor="start" style={monoFont} fontSize="9" fill="#9aa4b2">tension →</text>
    </svg>
  );
};

// ─── Custom profile modal — type each node temperature directly ───────────────
const CustomProfileModal: React.FC<{
  initial: number[]; h1: number;
  onApply: (prof: number[]) => void; onClose: () => void;
}> = ({ initial, h1, onApply, onClose }) => {
  const [draft, setDraft] = useState<number[]>([...initial]);
  const setNode = (j: number, v: number) => setDraft(d => { const n = [...d]; n[j] = v; return n; });

  // Preview geometry (compact SVG mirroring the main figure's temperature panel).
  const cMin = Math.min(...draft), cMax = Math.max(...draft);
  const lo = cMin, hi = cMax, pad = Math.max(4, (hi - lo) * 0.25);
  const pMin = lo - pad, pMax = hi + pad;
  const PW = 140, PH = 372, px0 = 8, px1 = PW - 8, py0 = 14, py1 = PH - 14;
  const pX = (t: number) => px0 + ((t - pMin) / (pMax - pMin || 1)) * (px1 - px0);
  const pY = (j: number) => py0 + (j / 10) * (py1 - py0);
  const path = draft.map((t, j) => `${j === 0 ? 'M' : 'L'}${pX(t).toFixed(1)},${pY(j).toFixed(1)}`).join(' ');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <span style={serifFont} className="text-lg font-semibold text-[#001f5b]">Custom Temperature Profile</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1 rounded">
            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 max-h-[72vh] overflow-y-auto">
          <p className="text-sm text-gray-500 mb-4">
            Enter the slab temperature at each depth, from the surface down to the bottom of the slab (°F).
          </p>
          <div className="flex gap-5">
            <div className="flex-1 flex flex-col gap-1.5">
              {draft.map((t, j) => {
                const z = (j / 10) * h1;
                const tag = j === 0 ? 'surface' : j === 10 ? 'bottom' : '';
                return (
                  <div key={j} className="flex items-center gap-2">
                    <span style={monoFont} className="text-xs text-gray-400 w-24 flex-shrink-0 text-right">
                      {z.toFixed(2)}″{tag && <span className="text-[#003594]"> · {tag}</span>}
                    </span>
                    <NumInput value={t} onChange={v => setNode(j, v)} step="0.5"
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#003594]/30 focus:border-[#003594] transition-colors" />
                  </div>
                );
              })}
            </div>
            <div className="flex-shrink-0 hidden sm:block">
              <p style={monoFont} className="text-[0.6rem] uppercase tracking-widest text-gray-400 mb-1 text-center">Preview</p>
              <svg viewBox={`0 0 ${PW} ${PH}`} width={PW} height={PH} className="border border-gray-100 rounded-lg bg-gray-50/50">
                <line x1={px0} y1={py0} x2={px0} y2={py1} stroke="#e4e8ee" strokeWidth="1" />
                <path d={path} fill="none" stroke={PITT_NAVY} strokeWidth="2" />
                {draft.map((t, j) => <circle key={j} cx={pX(t)} cy={pY(j)} r="2.5" fill={PITT_NAVY} />)}
                <text x={PW / 2} y={py0 - 2} textAnchor="middle" style={monoFont} fontSize="7" fill="#9aa4b2">surface</text>
                <text x={PW / 2} y={PH - 2} textAnchor="middle" style={monoFont} fontSize="7" fill="#9aa4b2">bottom</text>
              </svg>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50/60">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded border border-gray-300 text-gray-600 bg-white hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button onClick={() => { onApply(draft); onClose(); }}
            className="px-4 py-2 text-sm font-semibold rounded bg-[#003594] text-white hover:bg-[#00276e] transition-colors">
            Apply profile
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const NonlinearTempPage: React.FC = () => {
  const [bond, setBond]   = useState<Bond>('bonded');
  const [h1, setH1]       = useState(DEFAULTS.h1);
  const [h2, setH2]       = useState(DEFAULTS.h2);
  const [E1, setE1]       = useState(DEFAULTS.E1);
  const [E2, setE2]       = useState(DEFAULTS.E2);
  const [mu, setMu]       = useState(DEFAULTS.mu);
  const [alpha6, setAlpha6] = useState(DEFAULTS.alpha * 1e6); // stored as ×10⁻⁶
  const [T, setT]         = useState<number[]>(DEFAULT_PROFILE);

  const [showMat, setShowMat]   = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [activePreset, setActivePreset] = useState<PresetKey>('daytime');
  const [domain, setDomain]     = useState<[number, number]>(() => fitDomain(DEFAULT_PROFILE));
  const dragging = useRef(false);

  function fitDomain(prof: number[]): [number, number] {
    const lo = Math.min(...prof), hi = Math.max(...prof);
    const pad = Math.max(4, (hi - lo) * 0.25);
    return [Math.floor((lo - pad) / 2) * 2, Math.ceil((hi + pad) / 2) * 2];
  }

  const result = useMemo(
    () => computeNonlinTemp({ T, E1, mu, E2, alpha: alpha6 * 1e-6, h1, h2, bond }),
    [T, E1, mu, E2, alpha6, h1, h2, bond],
  );

  const applyProfile = (prof: number[]) => { setT(prof); setDomain(fitDomain(prof)); };
  const selectPreset = (key: PresetKey, prof: number[]) => { setActivePreset(key); applyProfile(prof); };
  const applyCustom = (prof: number[]) => { setActivePreset('custom'); applyProfile(prof); };
  const handleDrag = (node: number, temp: number) => {
    dragging.current = true;
    setActivePreset('custom');
    setT(prev => { const next = [...prev]; next[node] = Math.round(temp * 2) / 2; return next; });
  };
  const handleDragEnd = () => { dragging.current = false; setDomain(fitDomain(T)); };

  const tTop = T[0], tBot = T[N_NODES - 1];
  const presets: { key: PresetKey; label: string; prof: number[] }[] = [
    { key: 'daytime',   label: 'Daytime (+)',   prof: curvedProfile(97, 75, 1.6).map(v => Math.round(v * 2) / 2) },
    { key: 'nighttime', label: 'Nighttime (−)', prof: curvedProfile(72, 84, 1.6).map(v => Math.round(v * 2) / 2) },
    { key: 'linear',    label: 'Linear',        prof: linearProfile(tTop, tBot).map(v => Math.round(v * 2) / 2) },
  ];

  const statusDot = 'bg-green-400';

  return (
    <div id="nonlin-temp-app" className="min-h-screen bg-[#f0f2f5]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;1,400&family=DM+Mono:wght@400;500&family=Outfit:wght@400;500;600&display=swap');
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <Navbar />

      {/* Header */}
      <div className="bg-[#001f5b] border-b-2 border-[#FFB81C]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-end justify-between">
          <div>
            <p style={monoFont} className="text-[#FFB81C] text-xs tracking-[0.18em] uppercase mb-1.5 opacity-80">
              Rigid Pavements · Analysis
            </p>
            <h1 style={serifFont} className="text-4xl font-light text-white leading-none">Non-Linear Temperature Stresses</h1>
            <p className="text-white/50 text-sm mt-1.5">Self-equilibrating slab stresses from a non-linear temperature gradient</p>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot}`} />
            <span style={monoFont} className="text-sm text-green-300">Live</span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-5">
        <Instructions />

        {/* ── Inputs ─────────────────────────────────────────────────────────── */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.07)]">
          <div className="px-6 py-5 space-y-6">
            <div>
              <SectionHead>Slab &amp; Base</SectionHead>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Bond condition">
                  <div className="flex rounded border border-gray-300 overflow-hidden">
                    {(['bonded', 'unbonded'] as Bond[]).map(b => (
                      <button key={b} onClick={() => setBond(b)}
                        className={`flex-1 py-2 text-sm font-medium capitalize transition-colors ${bond === b ? 'bg-[#003594] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        {b}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Slab thickness, h₁ (in)">
                  <NumInput value={h1} onChange={setH1} step="0.5" min="4" max="20" />
                </Field>
                <Field label="Base thickness, h₂ (in)">
                  <NumInput value={h2} onChange={setH2} step="0.5" min="0" max="24" />
                </Field>
                <Field label="Material properties">
                  <button onClick={() => setShowMat(s => !s)}
                    className="w-full py-2 text-sm font-medium rounded border border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                    {showMat ? 'Hide' : 'Edit'} concrete properties
                    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13" className={`transition-transform ${showMat ? 'rotate-180' : ''}`}>
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </Field>
              </div>

              {showMat && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-gray-50 rounded-xl p-4 animate-[fadeIn_0.2s_ease-out]">
                  <Field label="PCC modulus, E₁ (psi)">
                    <NumInput value={E1} onChange={setE1} step="100000" min="1000000" />
                  </Field>
                  <Field label="Base modulus, E₂ (psi)">
                    <NumInput value={E2} onChange={setE2} step="50000" min="1000" />
                  </Field>
                  <Field label="Poisson's ratio, μ">
                    <NumInput value={mu} onChange={setMu} step="0.01" min="0.05" max="0.30" />
                  </Field>
                  <Field label={<>CTE (×10⁻⁶/°F)</>}>
                    <NumInput value={alpha6} onChange={setAlpha6} step="0.1" min="3" max="7" />
                  </Field>
                </div>
              )}
            </div>

            <div>
              <SectionHead>Temperature Profile</SectionHead>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <Field label="Surface temp, T_top (°F)">
                  <NumInput value={tTop} onChange={v => applyCustom(curvedProfileFromCurrent(T, v, tBot))} step="1" />
                </Field>
                <Field label="Bottom temp, T_bot (°F)">
                  <NumInput value={tBot} onChange={v => applyCustom(curvedProfileFromCurrent(T, tTop, v))} step="1" />
                </Field>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Presets</label>
                  <div className="flex gap-2">
                    {presets.map(p => {
                      const active = activePreset === p.key;
                      return (
                        <button key={p.key} onClick={() => selectPreset(p.key, p.prof)}
                          aria-pressed={active}
                          className={`flex-1 py-2 text-sm font-medium rounded border transition-colors ${
                            active
                              ? 'bg-[#003594] text-white border-[#003594]'
                              : 'border-gray-300 text-gray-600 bg-white hover:border-[#003594] hover:text-[#003594]'
                          }`}>
                          {p.label}
                        </button>
                      );
                    })}
                    <button onClick={() => setShowCustom(true)} aria-pressed={activePreset === 'custom'}
                      className={`flex-1 py-2 text-sm font-medium rounded border transition-colors flex items-center justify-center gap-1.5 ${
                        activePreset === 'custom'
                          ? 'bg-[#003594] text-white border-[#003594]'
                          : 'border-gray-300 text-gray-600 bg-white hover:border-[#003594] hover:text-[#003594]'
                      }`}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                      Custom…
                    </button>
                  </div>
                </div>
              </div>
              <p style={monoFont} className="text-xs text-gray-400 mt-3">
                Drag any point on the temperature curve in the figure below to shape a custom gradient.
              </p>
            </div>
          </div>
        </section>

        {/* ── Results ────────────────────────────────────────────────────────── */}
        <div className="space-y-5 animate-[fadeIn_0.25s_ease-out]">
          <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.07)] p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 style={serifFont} className="text-2xl font-semibold text-[#001f5b]">Results</h2>
              <span style={monoFont} className="text-xs text-gray-400 capitalize">{bond} · h₁ {h1}″ / h₂ {h2}″</span>
            </div>

            {/* Outcome card — the stresses */}
            <div className="rounded-2xl bg-[#001f5b] overflow-hidden shadow-[0_4px_20px_rgba(0,31,91,0.18)] mb-6">
              <div className="h-1 bg-[#FFB81C]" />
              <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 sm:divide-x sm:divide-white/15">
                {[
                  { label: 'Surface stress', v: result.signTop },
                  { label: 'Bottom-of-slab stress', v: result.signBot },
                ].map(({ label, v }, i) => (
                  <div key={label} className={`py-2 ${i === 0 ? 'sm:pr-8' : 'sm:pl-8 mt-4 sm:mt-0'}`}>
                    <p style={monoFont} className="text-[0.65rem] uppercase tracking-[0.15em] text-[#FFB81C] mb-1.5">{label}</p>
                    <p style={serifFont} className="text-4xl font-semibold text-white leading-none">
                      {v >= 0 ? '+' : '−'}{Math.abs(v).toFixed(1)}
                      <span className="text-xl font-normal text-white/55 ml-1.5">psi</span>
                    </p>
                    <p style={monoFont} className="text-[0.7rem] text-white/45 mt-1.5">{v >= 0 ? 'tension' : 'compression'}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed mb-6 -mt-2 px-1">
              These are the <strong>self-equilibrating (non-linear) thermal stresses only</strong> — the
              stress locked in by the non-linear part of the gradient. They are not the total stress:
              add them to the bending stresses from a load + equivalent-linear-ΔT (Dteff) analysis to
              get the resultant. The uniform-expansion part produces no stress in an unrestrained slab.
            </p>

            {/* Secondary intermediates */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              {[
                { label: 'Equivalent linear ΔT', v: `${result.Dteff.toFixed(1)} °F`, note: 'for curling analysis' },
                { label: 'Peak stress magnitude', v: `${result.sigmaMax.toFixed(1)} psi`, note: 'over the depth' },
                { label: 'Effective thickness, hₑ', v: `${result.heff.toFixed(2)} in`, note: 'governs the stresses' },
              ].map(({ label, v, note }) => (
                <div key={label} className="rounded-xl border border-gray-200 px-4 py-3">
                  <p style={monoFont} className="text-[0.62rem] uppercase tracking-[0.12em] text-gray-500 mb-1">{label}</p>
                  <p style={monoFont} className="text-lg text-[#001f5b] tabular-nums">{v}</p>
                  <p className="text-[0.68rem] text-gray-400 mt-0.5">{note}</p>
                </div>
              ))}
            </div>

            <SectionHead>Profile &amp; Stress</SectionHead>
            <div className="mx-auto" style={{ maxWidth: 780 }}>
              <SlabFigure
                T={T} h1={h1} h2={h2} heff={result.heff}
                sigma={result.sigma} sigmaMax={result.sigmaMax} x={result.x}
                signTop={result.signTop} signBot={result.signBot}
                domain={domain} onDrag={handleDrag} onDragEnd={handleDragEnd}
              />
              <p className="text-xs text-gray-500 leading-relaxed mt-3 max-w-2xl mx-auto text-center">
                The slab and base are combined by the transformed-section method into one
                homogeneous plate of effective thickness{' '}
                <span style={monoFont} className="text-[#003594]">hₑ = {result.heff.toFixed(1)}″</span>
                {' '}(from physical h₁ = {h1}″, h₂ = {h2}″), and the stresses are computed on that
                section. The base carries no gradient — it is taken as isothermal at the
                PCC-bottom temperature — so its {h2}″ physical thickness contributes only{' '}
                {(result.heff - h1).toFixed(1)}″ of effective thickness here.
              </p>
            </div>
          </section>
        </div>
      </main>

      {showCustom && (
        <CustomProfileModal
          initial={T} h1={h1}
          onApply={applyCustom}
          onClose={() => setShowCustom(false)}
        />
      )}

      <Footer />
    </div>
  );
};

/**
 * Re-shape the profile to new top/bottom temps while preserving its current
 * nonlinearity, so editing the endpoints keeps the curve's character.
 */
function curvedProfileFromCurrent(cur: number[], newTop: number, newBot: number): number[] {
  const oldTop = cur[0], oldBot = cur[N_NODES - 1];
  const span = oldTop - oldBot;
  return cur.map((v, j) => {
    const shape = span !== 0 ? (v - oldBot) / span : 1 - j / 10;
    return Math.round((newBot + (newTop - newBot) * shape) * 2) / 2;
  });
}

export default NonlinearTempPage;